/**
 * Serialization utilities for parsing and serializing recipe text with tokens
 */

import {Token, TokenType, ParsedRecipe} from "../models/editor.types";

/**
 * Escape HTML special characters
 */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Parse serialized recipe text with <t k="...">...</t> tags into plain text and tokens
 */
export function parseSerializedRecipe(serialized: string): ParsedRecipe {
  if (!serialized || !serialized.includes("<t")) {
    return {text: serialized ?? "", tokens: []};
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<root>${serialized}</root>`, "text/html");

  const root = doc.body.firstElementChild;
  if (!root) return {text: serialized, tokens: []};

  const outTokens: Token[] = [];
  let outText = "";

  const pushText = (s: string) => {
    outText += s;
  };

  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      pushText(node.nodeValue ?? "");
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();

    if (tag === "br") {
      pushText("\n");
      return;
    }

    if (tag === "t") {
      const k = (el.getAttribute("k") || "").toLowerCase();
      const tokenType: TokenType | null =
        k === "ingredient" ? "ingredient" :
        k === "quantity" ? "quantity" : null;

      const from = outText.length;

      for (const child of Array.from(el.childNodes)) walk(child);

      const to = outText.length;

      if (tokenType && to > from) {
        outTokens.push({from, to, type: tokenType});
      }
      return;
    }

    for (const child of Array.from(el.childNodes)) walk(child);
  };

  for (const child of Array.from(root.childNodes)) walk(child);

  return {text: outText, tokens: outTokens};
}

/**
 * Serialize plain text with tokens into tagged format
 */
export function serializeRecipe(text: string, tokens: Token[]): string {
  const filtered = tokens.filter((t) => t.type !== "ingredient_draft");
  const sorted = filtered.sort((a, b) => a.from - b.from);

  let serialized = "";
  let pos = 0;

  for (const t of sorted) {
    if (t.from < pos) continue;

    serialized += text.slice(pos, t.from);
    const seg = text.slice(t.from, t.to);

    if (t.type === "quantity") {
      serialized += `<t k="quantity">${escapeHtml(seg)}</t>`;
    }
    if (t.type === "ingredient") {
      serialized += `<t k="ingredient">${escapeHtml(seg)}</t>`;
    }

    pos = t.to;
  }

  serialized += text.slice(pos);
  return serialized;
}

/**
 * Extract serialized content from clipboard HTML
 */
export function extractSerializedFromClipboardHtml(html: string): string | null {
  if (!html) return null;

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  const wrapper = doc.querySelector('div[data-recipe-editor="v1"]');
  if (!wrapper) return null;

  return wrapper.innerHTML || "";
}

/**
 * Parse serialized recipe from clipboard (handles HTML fragments)
 */
export function parseSerializedRecipeFromClipboard(serialized: string): ParsedRecipe {
  return parseSerializedRecipe(serialized);
}
